import { useState, useCallback, MouseEvent, useEffect } from 'react'
import { Form } from 'react-final-form'
import { avatarInitials } from '../../../helpers/avatarPalette'
import Button from '../Button'
import Icon from '../Icon'
import InputLabel from '../InputLabel'
import Text from '../Text'
import TextField from '../TextField'
import styles from './MessageModal.module.scss'

interface IMessageModalProps {
  isOpen: boolean
  onClose: () => void
  onSend: (values: { subject: string; message: string }) => Promise<void>
  managerEmail?: string
  managerName?: string
  managerAvatar?: string | null
  isDiligenceMode?: boolean
}

const MessageModal = ({
  isOpen,
  onClose,
  onSend,
  managerName,
  managerAvatar,
  isDiligenceMode = false,
}: IMessageModalProps) => {
  const [isSending, setIsSending] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [avatarError, setAvatarError] = useState(false)

  const handleSendMessage = useCallback(
    async (values: { message: string }) => {
      setIsSending(true)
      try {
        await onSend({
          subject: 'Message from Client Portal',
          message: values.message,
        })
        setShowSuccess(true)
      } finally {
        setIsSending(false)
      }
    },
    [onSend],
  )

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false)
        onClose()
      }, 4000)
      return () => clearTimeout(timer)
    }
  }, [showSuccess, onClose])

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false)
    } else {
      setAvatarError(false)
    }
  }, [isOpen])

  const handleAvatarError = useCallback(() => {
    setAvatarError(true)
  }, [])

  const handleBackdropClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose],
  )

  const handleModalClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
  }, [])

  if (!isOpen) return null

  return (
    <>
      <div className={styles.backdrop} onClick={handleBackdropClick} />
      <div className={styles.messageModal} onClick={handleModalClick}>
        <div className={styles.closeButton} onClick={onClose}>
          <Icon name="x" size="xs" />
        </div>

        {showSuccess ? (
          <div className={styles.successContent}>
            <Icon name="check-circle" size="lg" className={styles.successIcon} />
            <Text
              variant="display"
              tone="primary"
              casing="sentence"
              component="span"
              className={styles.successTitle}
            >
              Message sent!
            </Text>
            <Text
              variant="bodyLg"
              tone="secondary"
              component="span"
              className={styles.successSubtitle}
            >
              {isDiligenceMode
                ? 'We will be in touch soon. Check your email for a response.'
                : `${managerName?.split(' ')[0] || 'Your Relationship Manager'} will be in touch soon. Check your email for a response.`}
            </Text>
          </div>
        ) : (
          <Form
            onSubmit={handleSendMessage}
            initialValues={{ message: '' }}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit} className={styles.messageModalContent}>
                <Text
                  variant="modalTitle"
                  tone="primary"
                  component="span"
                  className={styles.modalTitle}
                >
                  Get in touch
                </Text>
                <div className={styles.subtitleWithAvatar}>
                  {!isDiligenceMode &&
                    (managerAvatar && !avatarError ? (
                      <img
                        src={managerAvatar}
                        alt={managerName}
                        className={styles.avatarImage}
                        onError={handleAvatarError}
                      />
                    ) : (
                      <div className={styles.avatar}>{avatarInitials(managerName)}</div>
                    ))}
                  <span className={styles.modalSubtitle}>
                    {isDiligenceMode
                      ? 'Questions or feedback? Email our team right from Atlas'
                      : `Questions or feedback? Email ${managerName || 'your Relationship Manager'} right from Atlas`}
                  </span>
                </div>

                <div className={styles.formGroupMessage}>
                  <InputLabel className={styles.formLabel}>Message</InputLabel>
                  <TextField name="message" placeholder="Enter your message" multiline rows={8} />
                </div>

                <Button type="submit" className={styles.sendButton} loading={isSending}>
                  Send message
                </Button>
              </form>
            )}
          />
        )}
      </div>
    </>
  )
}

export default MessageModal
